import * as Sentry from '@sentry/react';
import { SentryTags } from '@sparrow-card/sparrow-card-constants';
import applicationConfig from 'config/applicationConfig';
import { type ErrorObject } from 'types/ErrorObject';
import { parseBoolean } from './parseBoolean';

const isErrorObject = (error: unknown): error is Error | ErrorObject => {
  return error instanceof Error || (typeof error === 'object' && !!error);
};

export const initializeSentry = (): void => {
  const isEnabled = parseBoolean(import.meta.env.VITE_SENTRY_LOGGING_ENABLED);

  Sentry.init({
    enabled: isEnabled,
    environment: import.meta.env.VITE_ENVIRONMENT,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    normalizeDepth: 10,
    integrations: [
      Sentry.browserTracingIntegration({
        instrumentPageLoad: true,
        instrumentNavigation: false,
      }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.breadcrumbsIntegration(),
    ],
    tracesSampleRate: 1.0,
    beforeSend: (event, hint) => {
      const processedEvent = { ...event };

      const cause = typeof hint.originalException === 'object' ? hint.originalException : undefined;

      if (isErrorObject(cause)) {
        if (cause.name === 'ValidationException') {
          return null;
        }

        processedEvent.extra = {
          ...processedEvent.extra,
          cause: {
            name: cause?.name,
            message: cause?.message,
            stack: cause?.stack,
          },
        };
      }

      return processedEvent;
    },
  });

  Sentry.getCurrentScope().setTag(SentryTags.SessionId, applicationConfig.sessionId);
};
